import { Component, Input, OnDestroy, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormArray, FormsModule, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { DfDatepickerRangeDirective, DfDatepickerRangeService } from '@design-factory/design-factory';
import {
  NgbDate,
  NgbDatepickerConfig,
  NgbCalendar,
  NgbDatepicker,
  NgbDropdown,
  NgbDropdownAnchor,
  NgbDropdownMenu,
  NgbInputDatepicker
} from '@ng-bootstrap/ng-bootstrap';
import { REQUIRED_MSG } from '../../service/model/common/validation-messages';
import { Date, ValidityApplicationPeriod, validityApplicationWeekdays } from './validity-application-period-utils';
import { Subscription } from 'rxjs';
import { NgClass } from '@angular/common';

const weekdays = ['MO', 'TU', 'WE', 'TH', 'FR'];
const weekends = ['SA', 'SU'];
@Component({
  selector: 'ama-ng-upp-rule-date-range',
  templateUrl: './rule-date-range.component.html',
  styleUrl: './rule-date-range.component.scss',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    NgbDropdown,
    DfDatepickerRangeDirective,
    NgbInputDatepicker,
    NgClass,
    NgbDropdownAnchor,
    NgbDropdownMenu,
    NgbDatepicker
  ]
})
export class RuleDateRangeComponent implements OnInit, OnChanges, OnDestroy {
  @Input() applicabilityFormGroup!: UntypedFormGroup;
  @Input() readonly!: boolean;

  validityApplicationWeekdays = validityApplicationWeekdays;
  selectedWeekdays: string[] = [];

  REQUIRED_MSG = REQUIRED_MSG.required();

  private readonly valueChangesSubscription = new Subscription();

  constructor(
    public dfDatepickerRangeService: DfDatepickerRangeService,
    private readonly dpConfig: NgbDatepickerConfig,
    private readonly calendar: NgbCalendar
  ) {}

  ngOnInit() {
    this.initializeCalendar();
    this.valueChangesSubscription.add(
      this.applicabilityFormGroup.valueChanges.subscribe(() => {
        this.validateDateRange();
      })
    );
    this.updateValidators();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.readonly && !changes.readonly.firstChange) {
      // When changing from readonly to not readonly, reinitialize if no value exists
      if (changes.readonly.previousValue === true && !this.readonly) {
        const currentValue = this.applicabilityFormGroup?.value?.validityApplicationPeriods?.[0];
        if (!currentValue?.start) {
          this.initializeCalendar();
        }
      }
      this.updateValidators();
    }
  }

  get today() {
    return this.calendar.getToday();
  }

  get formValidityApplicationWeekdays() {
    return this.applicabilityFormGroup?.controls?.validityApplicationWeekdays?.value as string[];
  }

  get startDate() {
    return this.applicabilityFormGroup?.value?.validityApplicationPeriods?.value?.[0]?.start;
  }

  get startDateControl() {
    const periodsArray = this.applicabilityFormGroup?.controls?.validityApplicationPeriods as FormArray;
    return periodsArray?.at(0)?.get('start') ?? undefined;
  }

  onFromDateInput(fromDate: string) {
    this.dfDatepickerRangeService.updateFrom(fromDate);
    this.updateValidityApplicationPeriods();
    if (!fromDate.length) {
      this.startDateControl?.markAsTouched();
    }
  }

  onToDateInput(toDate: string) {
    this.dfDatepickerRangeService.updateTo(toDate);
    this.updateValidityApplicationPeriods();
  }

  onCalendarToggle(isOpened: boolean) {
    this.dfDatepickerRangeService.openChange(isOpened);

    if (!isOpened) {
      this.startDateControl?.markAsTouched();
      this.updateValidityApplicationPeriods();
    }
  }

  getNgbDpStartDate(startDate: NgbDate | null): Date {
    if (startDate) {
      return {
        year: startDate.year,
        month: startDate.month,
        day: startDate.day
      };
    }
    return this.dpConfig.startDate as Date;
  }

  toggleDay(day: string, event: any) {
    const weekDaysArray = this.formValidityApplicationWeekdays || [];

    if (event.target.checked) {
      if (weekDaysArray.length === 6 && !weekDaysArray.includes(day)) {
        weekDaysArray.splice(0, weekDaysArray.length);
        event.preventDefault();
      } else if (!weekDaysArray.includes(day)) {
        weekDaysArray.push(day);
      }
    } else {
      const index = weekDaysArray.indexOf(day);
      if (index !== -1) {
        weekDaysArray.splice(index, 1);
      }
    }

    this.applicabilityFormGroup?.controls?.validityApplicationWeekdays?.setValue([...weekDaysArray]);
    this.selectedWeekdays = [...weekDaysArray];
  }

  clearDateRange() {
    this.dfDatepickerRangeService.fromDate = this.today;
    this.dfDatepickerRangeService.toDate = null;
    this.updateValidityApplicationPeriods();
    this.selectedWeekdays = [];
    this.applicabilityFormGroup?.controls?.validityApplicationWeekdays?.setValue([]);
  }

  setCalendarValues(startDate: string, endDate: string | null) {
    this.selectedWeekdays = this.formValidityApplicationWeekdays || [];
    this.dfDatepickerRangeService.fromDate = startDate?.length ? this.convertISOStringToNgbDate(startDate) : this.today;
    this.dfDatepickerRangeService.toDate = endDate ? this.convertISOStringToNgbDate(endDate) : null;
  }

  private initializeCalendar() {
    const validityApplicationPeriod = this.applicabilityFormGroup?.value?.validityApplicationPeriods?.[0];
    this.setCalendarValues(validityApplicationPeriod?.start, validityApplicationPeriod?.end);
    this.updateValidityApplicationPeriods();
  }

  private isWeekdaysSelected(selectedDays: string[]): boolean {
    return weekdays.every((day) => selectedDays?.includes(day));
  }

  private isWeekendsSelected(selectedDays: string[]): boolean {
    return weekends.every((day) => selectedDays?.includes(day));
  }

  getBadges(): string[] {
    const selectedDays = this.formValidityApplicationWeekdays;

    const badges: string[] = [];
    if (selectedDays) {
      if (this.isWeekdaysSelected(selectedDays)) {
        badges.push('Weekdays');
      } else {
        const selectedWeekdays = selectedDays.filter((day) => weekdays.includes(day));
        badges.push(...selectedWeekdays.map((day) => this.getDisplayName(day)));
      }

      if (this.isWeekendsSelected(selectedDays)) {
        badges.push('Weekends');
      } else {
        const selectedWeekends = selectedDays.filter((day) => weekends.includes(day));
        badges.push(...selectedWeekends.map((day) => this.getDisplayName(day)));
      }
    }

    return badges;
  }

  private getDisplayName(day: string): string {
    const weekDay = this.validityApplicationWeekdays?.find((wd) => wd.value === day);
    return weekDay ? weekDay.displayName : '';
  }

  private updateValidityApplicationPeriods() {
    if (this.dfDatepickerRangeService.fromDate) {
      const formValidityPeriodsValue = this.parseDatesToFormValue(
        this.dfDatepickerRangeService.fromDate,
        this.dfDatepickerRangeService.toDate
      );
      this.applicabilityFormGroup?.controls?.validityApplicationPeriods?.patchValue(formValidityPeriodsValue);
    } else {
      this.applicabilityFormGroup?.controls?.validityApplicationPeriods?.patchValue([
        { start: undefined, end: undefined }
      ]);
    }
  }

  private parseDatesToFormValue(startDate: NgbDate, endDate: NgbDate | null): ValidityApplicationPeriod[] {
    return [
      {
        start: this.convertNgbDateToISOString(startDate),
        end: endDate ? this.convertNgbDateToISOString(endDate) : null
      }
    ];
  }
  private convertNgbDateToISOString(date: NgbDate): string {
    const jsDate = new Date(date.year, date.month - 1, date.day, 12);
    return jsDate.toISOString();
  }

  private convertISOStringToNgbDate(isoString: string): NgbDate {
    const jsDate = new Date(isoString);
    return new NgbDate(jsDate.getUTCFullYear(), jsDate.getUTCMonth() + 1, jsDate.getUTCDate());
  }

  private validateDateRange() {
    const fromDate = this.dfDatepickerRangeService.fromDate;
    const toDate = this.dfDatepickerRangeService.toDate;

    const dateRangeIsValid =
      fromDate &&
      toDate &&
      (toDate.year < fromDate.year ||
        (toDate.year === fromDate.year && toDate.month < fromDate.month) ||
        (toDate.year === fromDate.year && toDate.month === fromDate.month && toDate.day < fromDate.day));

    if (dateRangeIsValid) {
      this.applicabilityFormGroup.setErrors({ invalidDateRange: true });
    } else {
      this.applicabilityFormGroup.setErrors(null);
    }
  }

  private updateValidators(): void {
    if (this.startDateControl) {
      if (!this.readonly) {
        // Mark as untouched before enabling to prevent showing validation errors immediately
        this.startDateControl.markAsUntouched();
        this.startDateControl.enable({ emitEvent: false });
        this.startDateControl.setValidators([Validators.required]);
      } else {
        this.startDateControl.clearValidators();
        this.startDateControl.disable({ emitEvent: false });
        // Mark as untouched when disabling to reset any touched state from markAllAsTouched
        this.startDateControl.markAsUntouched();
      }
      this.startDateControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  ngOnDestroy(): void {
    if (this.valueChangesSubscription) {
      this.valueChangesSubscription.unsubscribe();
    }

    if (this.startDateControl) {
      this.startDateControl.clearValidators();
      this.startDateControl.updateValueAndValidity();
    }

    this.dfDatepickerRangeService.fromDate = null;
    this.dfDatepickerRangeService.toDate = null;
  }
}
